import { cloneElement, type ReactElement } from 'react';
import { CircleAlert } from 'lucide-react';
import { Label } from '@/components/ui/label';

type ControlProps = {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
};

/**
 * Label + control + hulptekst + fout (design-skill: FormField). Koppelt
 * hulptekst en fout via aria-describedby en zet aria-invalid op het control.
 * Toon de fout pas na blur of submit, nooit tijdens het typen.
 */
export function FormField(props: {
  id: string;
  label: string;
  description?: string;
  error?: string | null;
  optional?: boolean;
  children: ReactElement<ControlProps>;
}) {
  const { id, label, description, error, optional, children } = props;
  const descId = description && !error ? `${id}-desc` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [descId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>
        {label}
        {optional && <span className="font-normal text-muted-foreground">(optioneel)</span>}
      </Label>
      {cloneElement(children, {
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
      })}
      {descId && (
        <p id={descId} className="text-sm text-muted-foreground">
          {description}
        </p>
      )}
      {errorId && (
        <p id={errorId} className="flex items-start gap-1.5 text-sm font-medium text-destructive">
          <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}
